import dependencies from "../authentication/dependencies";
import {
  createJsonApiFilter,
  createJsonApiOrGroup,
} from "../constants/jsonapi_helpers";
import { uploadFileHeader } from "../cycles/headers";
import {
  DEVELOPER,
  FINANCIAL,
  LEGAL,
  ORGANIZATION_OWNER,
} from "../constants/organizationRoles";
import v4 from "uuid/v4";
import { postJson } from "./apiErrors";
const { baseUrl, newBaseUrl, generalHeader } = dependencies;

export const fetchOrganizations = async (params = {}) => {
  // Handle React Query context object
  let pageLimit = 50;
  let pageOffset = 0;
  let userId = null;

  if (typeof params === "object") {
    if (params.queryKey) {
      // When called from React Query, first param is context object
      // Extract actual params from queryKey if they exist
      const [, actualParams] = params.queryKey || [];
      if (actualParams) {
        pageLimit = actualParams.pageLimit || 50;
        pageOffset = actualParams.pageOffset || 0;
        userId = actualParams.userId || null;
      }
    } else {
      // Direct call with params object
      pageLimit = params.pageLimit || 50;
      pageOffset = params.pageOffset || 0;
      userId = params.userId || null;
    }
  }

  const url = newBaseUrl + "/Organization/list";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(), // Use actual userId or fallback to v4()
    },
    payload: {
      pageLimit: pageLimit,
      pageOffset: pageOffset,
      sortField: "created",
      sortOrder: "desc",
    },
  };

  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: generalHeader(null),
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const fetchOrganization = async (params = {}) => {
  // Handle React Query context object and direct calls
  let organizationId = null;
  let userId = null;

  // Check if params is a string (direct organizationId)
  if (typeof params === "string") {
    organizationId = params;
  } else if (typeof params === "object") {
    if (params.queryKey) {
      // When called from React Query with context object
      const [, queryParams] = params.queryKey || [];
      if (typeof queryParams === "string") {
        // Old format: just organizationId as string
        organizationId = queryParams;
      } else if (typeof queryParams === "object") {
        // New format: object with organizationId and userId
        organizationId = queryParams.organizationId;
        userId = queryParams.userId;
      }
    } else {
      // Direct call with params object
      organizationId = params.organizationId || params.id || null;
      userId = params.userId || null;
    }
  }

  // Use the dedicated get endpoint for fetching a single organization
  const url = newBaseUrl + "/Organization/get";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(), // Use actual userId or fallback to v4()
    },
    payload: {
      id: organizationId,
      title: null, // Optional field, can be null
    },
  };

  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: generalHeader(null),
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const fetchOrganizationMembers = async (uuid, id) => {
  // Members are now included in the Organization/get response
  // This function is kept for backward compatibility but can be deprecated
  // If we need to fetch members separately, we can use the Organization/get endpoint

  // For now, return empty data structure to avoid breaking existing code
  // The actual members data should come from the organization response
  return {
    data: [],
    included: [],
  };
};

export const fetchOrganizationRoles = async (userId) => {
  const url = newBaseUrl + "/OrganizationRole/list";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(), // Use actual userId or fallback to v4()
    },
    payload: {
      pageLimit: 50,
      pageOffset: 0,
      // Remove sortField as 'title' column doesn't exist in OrganizationRole table
      // Backend should use its default sorting
    },
  };

  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: generalHeader(null),
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const deleteOrganization = async (uuid, csrfToken, userId) => {
  const url = newBaseUrl + "/Organization/delete";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {
      id: uuid,
    },
  };

  return postJson(url, {
    headers: generalHeader(csrfToken),
    body: payload,
    category: url,
  });
};

export const fetchOrganizationContent = async (uuid, id) => {
  // Applications are now included in the Organization/get response
  // If we need to fetch applications separately, use Application/list with organization filter
  const url = newBaseUrl + "/Application/list";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: v4(),
    },
    payload: {
      pageLimit: 50,
      pageOffset: 0,
      sortField: "created",
      sortOrder: "desc",
      organizationId: uuid, // Filter by organization ID
    },
  };

  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: generalHeader(null),
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const updateOrganization = async (
  csrfToken,
  organizationId,
  field_documents,
  title,
  description,
  userId
) => {
  const url = newBaseUrl + "/Organization/update";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || "00000000-0000-0000-0000-000000000000",
    },
    payload: {
      id: organizationId,
      ...(title && { title }),
      ...(description && { description }),
      ...(field_documents && {
        documents: field_documents.map((docId) => ({
          id: docId,
        })),
      }),
    },
  };

  return postJson(url, {
    headers: generalHeader(csrfToken),
    body: payload,
    category: url,
  });
};

export const uploadOrganizationFile = async (csrfToken, data) => {
  const { documents, organizationId, userId } = data;
  const url = newBaseUrl + `/Attachment/create`;

  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Extract base64 data from data URL
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Upload files one by one and collect their IDs
  const documentsPromises = Array.from(documents).map(async (document) => {
    // Convert file to base64
    const fileData = await fileToBase64(document);

    const payload = {
      header: {
        id: v4(),
        application: v4(),
        bank: "NBG",
        userId: userId || v4(),
      },
      payload: {
        fileName: document.name,
        fileData: fileData,
      },
    };

    const response = await fetch(url, {
      method: "POST",
      headers: generalHeader(csrfToken, true), // Use JSON headers
      credentials: "include",
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    // Check for exception in response (API returns 200 with exception field)
    if (data && data.exception && Object.keys(data.exception).length > 0) {
      throw new Error(JSON.stringify(data.exception));
    }

    // Return the attachment ID in expected format
    // The API returns { payload: "attachment-id", exception: null }
    return {
      data: {
        id: data.payload,
      },
    };
  });

  return await Promise.all(documentsPromises);
};

export const acceptOrganizationInvitation = async (csrfToken, data, userId) => {
  const { organizationId } = data;
  // The invited user activates their own membership; the server matches the
  // pending invitation on the authenticated caller's email.
  const url = newBaseUrl + "/Organization/member/accept";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {
      organizationId: organizationId,
    },
  };

  return postJson(url, {
    headers: generalHeader(csrfToken),
    body: payload,
    category: url,
  });
};

export const updateOrganizationMembership = async (csrfToken, data, userId) => {
  const { organizationId, memberId, organizationRoles } = data;
  // Use the dedicated update endpoint for updating member roles
  const url = newBaseUrl + "/Organization/member/update";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {
      organizationId: organizationId,
      memberId: memberId,
      organizationRoles: organizationRoles,
    },
  };

  return postJson(url, {
    headers: generalHeader(csrfToken),
    body: payload,
    category: url,
  });
};

export const deleteOrganizationMembership = async (csrfToken, data, userId) => {
  const { membershipΙd, organizationId } = data;
  const url = newBaseUrl + "/Organization/member/delete";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {
      organizationId: organizationId,
      memberId: membershipΙd,
    },
  };

  return postJson(url, {
    headers: generalHeader(csrfToken),
    body: payload,
    category: url,
  });
};

export const createOrganizationMembership = async (
  csrfToken,
  data,
  userId,
  rolesById
) => {
  const url = newBaseUrl + "/Organization/member/invite";

  // Map legacy field names to new API format
  const organizationId = data.organizationId || data.gid;
  const roleIds = data.roles || data.group_roles || [];

  // Map role IDs to UUIDs
  const roleUUIDs = roleIds.map((roleId) => {
    // If rolesById is provided, try to get the UUID
    if (rolesById && rolesById[roleId]) {
      return rolesById[roleId].uuid;
    }
    // If it already looks like a UUID, return as is
    if (
      roleId.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      )
    ) {
      return roleId;
    }
    // Otherwise return the roleId (this might cause an error but will help debug)
    return roleId;
  });

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {
      organizationId: organizationId,
      invitations: [
        {
          email: data.email,
          organizationRoles: roleUUIDs,
        },
      ],
      created: new Date().toISOString(),
    },
  };

  return postJson(url, {
    headers: generalHeader(csrfToken),
    body: payload,
    category: url,
  });
};

export const resendMemberInvitation = async (csrfToken, data, userId) => {
  const { membershipId, gid, organizationId } = data;
  // Use the dedicated resend endpoint
  const url = newBaseUrl + "/Organization/member/resend";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {
      organizationId: organizationId || gid,
      memberId: membershipId,
    },
  };

  return postJson(url, {
    headers: generalHeader(csrfToken),
    body: payload,
    category: url,
  });
};

export const deleteOrganizationDocument = async (
  csrfToken,
  documentId,
  userId
) => {
  const url = newBaseUrl + "/Organization/document/delete";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {
      id: documentId,
    },
  };

  return postJson(url, {
    headers: generalHeader(csrfToken, true),
    body: payload,
    category: url,
  });
};
